import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';

const Overlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.md};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const Dialog = styled.div<{ $visible: boolean }>`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  max-width: 500px;
  width: 100%;
  transform: ${({ $visible }) => ($visible ? 'scale(1)' : 'scale(0.95)')};
  transition: transform 0.3s ease;
`;

const Header = styled.div`
  padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray.light};
`;

const Title = styled.h3<{ $variant: 'danger' | 'warning' | 'info' }>`
  margin: 0;
  color: ${({ theme, $variant }) =>
    $variant === 'danger' ? theme.colors.error :
    $variant === 'warning' ? theme.colors.warning :
    theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const IconSpan = styled.span<{ $variant: 'danger' | 'warning' | 'info' }>`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  color: ${({ theme, $variant }) =>
    $variant === 'danger' ? theme.colors.error :
    $variant === 'warning' ? theme.colors.warning :
    theme.colors.primary};
`;

const Body = styled.div`
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.xl};
`;

const Message = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.6;
`;

const Details = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.gray.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray.dark};
`;

const Footer = styled.div`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.xl};
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: flex-end;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column-reverse;
  }
`;

const Button = styled.button<{ $variant: 'primary' | 'danger' | 'secondary' }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: all ${({ theme }) => theme.transitions.fast};
  cursor: pointer;
  min-width: 100px;

  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'primary':
        return `
          background-color: ${theme.colors.primary};
          color: ${theme.colors.white};
          border: none;

          &:hover:not(:disabled) {
            background-color: ${theme.colors.secondary};
          }
        `;
      case 'danger':
        return `
          background-color: ${theme.colors.error};
          color: ${theme.colors.white};
          border: none;

          &:hover:not(:disabled) {
            background-color: #c53030;
          }
        `;
      case 'secondary':
        return `
          background-color: transparent;
          color: ${theme.colors.text};
          border: 1px solid ${theme.colors.gray.medium};

          &:hover:not(:disabled) {
            background-color: ${theme.colors.gray.light};
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 100%;
  }
`;

export interface ConfirmDialogProps {
  isOpen: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  title: string;
  message: string;
  details?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  loading?: boolean;
}

export function ConfirmDialog({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  details,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'info',
  loading = false,
}: ConfirmDialogProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      const timeout = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isProcessing) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isProcessing, onCancel]);

  const handleConfirm = async () => {
    try {
      setIsProcessing(true);
      await onConfirm();
    } catch (error) {
      console.error('Confirm action failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    if (!isProcessing) {
      onCancel();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isProcessing) {
      onCancel();
    }
  };

  const getIcon = () => {
    switch (variant) {
      case 'danger':
        return '⚠️';
      case 'warning':
        return '⚠️';
      case 'info':
      default:
        return 'ℹ️';
    }
  };

  const getConfirmButtonVariant = () => {
    switch (variant) {
      case 'danger':
        return 'danger';
      case 'warning':
        return 'danger';
      case 'info':
      default:
        return 'primary';
    }
  };

  if (!isVisible) return null;

  return createPortal(
    <Overlay $visible={isOpen} onClick={handleOverlayClick}>
      <Dialog $visible={isOpen} role="dialog" aria-modal="true" aria-labelledby="dialog-title">
        <Header>
          <Title $variant={variant} id="dialog-title">
            <IconSpan $variant={variant}>{getIcon()}</IconSpan>
            {title}
          </Title>
        </Header>

        <Body>
          <Message>{message}</Message>
          {details && <Details>{details}</Details>}
        </Body>

        <Footer>
          <Button
            $variant="secondary"
            onClick={handleCancel}
            disabled={isProcessing || loading}
          >
            {cancelText}
          </Button>
          <Button
            $variant={getConfirmButtonVariant()}
            onClick={handleConfirm}
            disabled={isProcessing || loading}
          >
            {isProcessing || loading ? 'Processing...' : confirmText}
          </Button>
        </Footer>
      </Dialog>
    </Overlay>,
    document.body
  );
}

// Hook for managing confirm dialogs
export interface UseConfirmDialogOptions {
  title: string;
  message: string;
  details?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
}

export function useConfirmDialog(options: UseConfirmDialogOptions) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<(() => void | Promise<void>) | null>(null);

  const confirm = (action: () => void | Promise<void>) => {
    setPendingConfirm(() => action);
    setIsOpen(true);
  };

  const handleConfirm = async () => {
    if (pendingConfirm) {
      try {
        setLoading(true);
        await pendingConfirm();
        setIsOpen(false);
        setPendingConfirm(null);
      } catch (error) {
        console.error('Confirm action failed:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    setPendingConfirm(null);
    setLoading(false);
  };

  const dialog = (
    <ConfirmDialog
      isOpen={isOpen}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      loading={loading}
      {...options}
    />
  );

  return { confirm, dialog, isOpen, loading };
}

// Predefined confirm dialogs for common actions
export function useDeleteConfirm(itemName: string = 'item') {
  return useConfirmDialog({
    title: 'Delete Confirmation',
    message: `Are you sure you want to delete this ${itemName}? This action cannot be undone.`,
    confirmText: 'Delete',
    cancelText: 'Keep',
    variant: 'danger',
  });
}

export function useDiscardChangesConfirm() {
  return useConfirmDialog({
    title: 'Discard Changes',
    message: 'You have unsaved changes. Are you sure you want to discard them?',
    confirmText: 'Discard',
    cancelText: 'Keep Editing',
    variant: 'warning',
  });
}

export function useLogoutConfirm() {
  return useConfirmDialog({
    title: 'Sign Out',
    message: 'Are you sure you want to sign out?',
    confirmText: 'Sign Out',
    cancelText: 'Stay Signed In',
    variant: 'info',
  });
}